import React, { useRef, useState } from 'react'
import { Drawer, Button, Alert, notification } from 'antd'
import SignatureCanvas from 'react-signature-canvas'

export type SignatureSubmitState = 'IDLE' | 'SAVING' | 'ERROR' | 'DONE'
export type SignatureSubmitEvent = 'SUBMIT' | 'SUCCESS' | 'FAILURE' | 'RETRY' | 'CLEAR'

export interface SignatureSubmitTransition {
	state: SignatureSubmitState
	clearCanvas: boolean
	canRetry: boolean
	closeDialog: boolean
}

/**
 * Podpis sa po zlyhani uploadu NIKDY nemaze (REQ-14-14). Zamestnanec uz raz podpisal - druhy raz
 * kreslit nemusi. Platno sa cisti len po uspesnom ulozeni alebo ked to pouzivatel sam vypyta.
 */
export const resolveSignatureSubmit = (
	state: SignatureSubmitState,
	event: SignatureSubmitEvent
): SignatureSubmitTransition => {
	if (event === 'SUBMIT') {
		return {
			state: 'SAVING', clearCanvas: false, canRetry: false, closeDialog: false
		}
	}

	if (event === 'FAILURE') {
		return {
			state: 'ERROR', clearCanvas: false, canRetry: true, closeDialog: false
		}
	}

	if (event === 'SUCCESS') {
		return {
			state: 'DONE', clearCanvas: true, canRetry: false, closeDialog: true
		}
	}

	if (event === 'RETRY') {
		return {
			state: 'SAVING', clearCanvas: false, canRetry: false, closeDialog: false
		}
	}

	// event === 'CLEAR'
	return {
		state: 'IDLE', clearCanvas: true, canRetry: false, closeDialog: false
	}
}

type Props = {
	visible: boolean
	saving: boolean
	errorMessage?: string | null
	onCancel: () => void
	onSave: (blob: Blob) => void
	onRetry?: () => void
}

const SignaturePad: React.FC<Props> = ({
	visible, saving, errorMessage, onCancel, onSave, onRetry
}) => {
	const sigCanvasRef = useRef<SignatureCanvas | null>(null)
	const [canvasReady, setCanvasReady] = useState(false)

	const handleClear = () => {
		sigCanvasRef.current?.clear()
	}

	const handleSave = () => {
		const pad = sigCanvasRef.current
		if (!pad || pad.isEmpty()) {
			notification.warning({ message: 'Podpis chýba', description: 'Podpíšte sa, prosím, do vyznačenej plochy.' })
			return
		}
		pad.getTrimmedCanvas().toBlob((blob: Blob | null) => {
			if (blob) onSave(blob)
		}, 'image/png')
	}

	return (
		<Drawer
			placement="bottom"
			height="100%"
			visible={visible}
			onClose={onCancel}
			closable={false}
			afterVisibleChange={(open) => setCanvasReady(open)}
			title="Podpis preberajúceho"
			footer={(
				<div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
					{saving && <span style={{ marginRight: 'auto' }}>Ukladám podpis a generujem PDF…</span>}
					<Button onClick={onCancel} disabled={saving}>Zrušiť</Button>
					<Button onClick={handleClear} disabled={saving}>Zopakovať</Button>
					<Button type="primary" size="large" loading={saving} disabled={saving} onClick={handleSave}>
						Uložiť podpis
					</Button>
				</div>
			)}
		>
			{errorMessage && (
				<Alert
					type="error"
					message={errorMessage}
					style={{ marginBottom: 16 }}
					action={onRetry ? (
						<Button size="small" danger onClick={onRetry}>Skúsiť znova</Button>
					) : undefined}
				/>
			)}
			{canvasReady && (
				<SignatureCanvas
					key={visible ? 'open' : 'closed'}
					ref={sigCanvasRef}
					canvasProps={{
						className: 'handover-signature-canvas',
						style: {
							touchAction: 'none', width: '100%', height: '340px', border: '1px solid #d9d9d9', borderRadius: 4, background: '#fff'
						}
					}}
				/>
			)}
			<div style={{ borderTop: '1px dashed #bfbfbf', margin: '0 24px' }} />
			<div style={{ textAlign: 'center', marginTop: 8, color: '#8c8c8c' }}>Podpis preberajúceho</div>
		</Drawer>
	)
}

export default SignaturePad
